import { useRef } from 'react'
import { ClientOnly } from '@tanstack/react-router'
import { Base } from './base'
import { BaseHover } from './base-hover'
import { Lines } from './lines'
import { ComparisonRings, IndicatorTitles, shownProfile } from './comparison-rings'
import { Scores } from './scores'
import type { Radar } from 'shared'
import { useRootSearch } from '@/hooks/use-root-search'
import { useRadarStyle } from '@/hooks/use-radar-style'
import { Skeleton } from '@/components/ui/skeleton'

export function Radar(props: {
  indicators: Radar['indicators'] | undefined
  radarOnSubSection?: boolean
  // The report's `selected` (compare/first/second), passed down from `ReportComponent` which already holds
  // the report — instead of this leaf re-subscribing to `getReport` just to read it. Re-reading here not only
  // duplicated a subscriber but read the LIVE key while the route reads the DEFERRED one, so during a
  // filter/type transition the radar eagerly pulled the new fetch and weakened the keep-previous UX.
  selected?: 'first' | 'second' | 'compare'
  // `still` is the marketing variant (the landing's hero): the rings drawing AND no hover titles or handlers.
  // `indicators` carries the base profile as `value` and the shown one as `valueToCompare`; every ring reads as its
  // own radar (ComparisonRings) with the shown profile's scores; in compare mode the base profile and its scores
  // join it.
  variant?: 'report' | 'still'
}) {
  const radarRef = useRef<SVGSVGElement>(null)
  const { selected: selectedFromSearch } = useRootSearch()
  const selected = props.selected ?? selectedFromSearch

  // Part of the subtrees' key, so crossing this boundary remounts rather than morphs them — docs/notes/app/radar.md.
  const mode = selected === 'compare' ? 'compare' : 'profile'
  // A super admin can preview the report in the rings drawing (user-menu.tsx). The preference lives in a cookie, so
  // the SERVER already knows it and renders the right drawing — no hydration gate, no classic→rings redraw (see
  // radar-style.ts). The SUBSECTION radars keep the classic drawing whatever the preference: `ComparisonRings` reads
  // all 18/6/3 indicators by index, and a subsection's set carries only its own.
  const radarStyle = useRadarStyle()
  // Two independent things the marketing variant happens to want together, kept apart here: WHICH DRAWING is on
  // screen (rings or the classic lines) and whether the radar is INTERACTIVE. The landing's hero is rings AND inert;
  // the report in the rings preference is rings AND still hovers its indicators, as the classic drawing does.
  const rings = props.variant === 'still' || (radarStyle === 'rings' && !props.radarOnSubSection)
  const interactive = props.variant !== 'still'

  const svg = (
    <svg
      ref={radarRef}
      viewBox="-50 -41 424 400"
      width="424px"
      height="340px"
      className="radar mx-auto h-auto max-h-[848px] w-full max-w-[848px]"
    >
      <Base radarOnSubSection={props.radarOnSubSection} indicators={props.indicators} />

      {props.indicators && rings && (
        // Each ring its own radar, with the scores of the coloured profile; the base profile joins only in compare.
        <>
          <ComparisonRings indicators={props.indicators} showBase={selected === 'compare'} />
          {/* The base profile's scores stay mounted and fade with its layer, on the same 250 ms, so they never pop in. */}
          <g style={{ opacity: selected === 'compare' ? 1 : 0, transition: 'opacity 250ms linear' }}>
            <Scores indicators={props.indicators} red />
          </g>
          {/* One instance across phases: in compare its bubbles glide aside to the report's side-by-side positions. */}
          <Scores
            indicators={selected === 'compare' ? props.indicators : shownProfile(props.indicators)}
            compare={selected === 'compare'}
            ringed
          />
          {/* Drawn here only where nothing else writes them: an interactive radar gets the very same titles from
                `BaseHover`, whose resting state IS the competences — rendering both would double every label. */}
          {!interactive && <IndicatorTitles indicators={props.indicators} />}
        </>
      )}

      {props.indicators && !rings && (
        <>
          {!props.radarOnSubSection && ( // Disable lines on subsections
            <>
              {selected === 'compare' && <Lines indicators={props.indicators} compare />}
              <Lines key={`lines-${mode}`} indicators={props.indicators} red={selected === 'compare'} />
            </>
          )}

          {selected === 'compare' && (
            <Scores radarOnSubSection={props.radarOnSubSection} indicators={props.indicators} compare />
          )}
          <Scores
            key={`scores-${mode}`}
            radarOnSubSection={props.radarOnSubSection}
            indicators={props.indicators}
            red={selected === 'compare'}
          />
        </>
      )}

      {/* Hide overflow scores at hover (hidden in print to avoid white artifacts) */}
      {interactive && (
        <circle
          className="hide-overflow-scores-at-hover animate print:hidden"
          cx="162"
          cy="162"
          r="125"
          stroke="transparent"
          strokeWidth="60"
          fill="none"
        />
      )}
      {interactive && <BaseHover forwardRef={radarRef} indicators={props.indicators} />}
    </svg>
  )

  // The MAIN radar is above the fold and its geometry derives deterministically from `indicators`, present
  // server-side under full SSR — so it renders directly, not client-only, and paints with no skeleton. Hydration
  // is safe: grid and lines match, the score <tspan>s carry the same numbers on both passes, and neither pass
  // emits any <animate> at all, since a first render has no previous geometry to morph away from.
  if (!props.radarOnSubSection || !interactive) return svg

  // Client-only, and the fallback's disc is sized off the grid rather than the wide box — docs/notes/app/radar.md.
  return (
    <ClientOnly
      fallback={
        <div className="mx-auto flex aspect-[424/340] w-full max-w-[848px] items-center justify-center">
          <Skeleton className="aspect-square h-[77.5%] rounded-full" />
        </div>
      }
    >
      {svg}
    </ClientOnly>
  )
}
